import { formatDate, isToday } from "@clipboard-health/date-time";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { formatTimeWithTimezoneDisambiguation } from "@src/appV2/redesign/utils/formatTimeWithTimezoneDisambiguation";
import { parseISO, subMinutes } from "date-fns";
import { useMemo } from "react";

interface TooEarlyToClockInBottomSheetProps {
  modalState: UseModalState;
  shiftStart: string;
  workplaceTimezone: string | undefined;
  earlyClockInEnabledInMinutes: number | undefined;
}

export function TooEarlyToClockInBottomSheet(props: TooEarlyToClockInBottomSheetProps) {
  const { modalState, shiftStart, workplaceTimezone, earlyClockInEnabledInMinutes } = props;

  const userTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  const effectiveTimezone = workplaceTimezone ?? userTimezone;

  const { formattedTime, formattedDate } = useMemo(() => {
    const shiftStartDate = parseISO(shiftStart);
    const earliestClockInTime = isDefined(earlyClockInEnabledInMinutes)
      ? subMinutes(shiftStartDate, earlyClockInEnabledInMinutes)
      : shiftStartDate;

    return {
      formattedTime: formatTimeWithTimezoneDisambiguation({
        time: earliestClockInTime,
        displayInTimezone: effectiveTimezone,
        compareToTimezone: userTimezone,
      }),
      formattedDate: isToday(earliestClockInTime, { timeZone: effectiveTimezone })
        ? undefined
        : formatDate(earliestClockInTime, { timeZone: effectiveTimezone }),
    };
  }, [shiftStart, effectiveTimezone, userTimezone, earlyClockInEnabledInMinutes]);

  return (
    <ThemeProvider theme={getTheme()}>
      <BottomSheet
        modalState={modalState}
        footer={
          <DialogFooter orientation="vertical" sx={{ my: 5 }}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                modalState.closeModal();
              }}
            >
              Got it
            </Button>
          </DialogFooter>
        }
      >
        <BottomSheetIllustrationContent
          illustrationType="clock-in-pending"
          title="It's too early to clock in"
          description={`Clock in opens at ${formattedTime}${isDefined(formattedDate) ? ` on ${formattedDate}` : ""}. If you were asked to arrive early, ask a supervisor to update the shift's start time.`}
        />
      </BottomSheet>
    </ThemeProvider>
  );
}
